import { call, put, all, takeEvery, throttle } from 'redux-saga/effects';
import {
  defineKqlQuery,
  fetchForm,
  searchSubmissions,
  fetchSubmission,
  createSubmission,
  updateSubmission,
  deleteSubmission,
  updateForm,
  deleteForm,
} from '@kineticdata/react';
import { addToast, addToastAlert } from '@kineticdata/bundle-common';
import {
  actions,
  types,
  FORM_INCLUDES,
  FORM_FULL_INCLUDES,
  SUBMISSION_INCLUDES,
} from '../modules/adminForms';
import { Range } from 'immutable';

/**
 * Take a large List and return a Sequence of List.  Will spilt List into equal chunks.
 * Last chunk may be smaller if split can't be done evenly.
 *
 * @param {List} list - List of elements
 * @param {number} [chunkSize=1] - Desired size of chunks
 * @returns {List} - List of List elements
 */
export const chunkList = (list, chunkSize = 1) =>
  Range(0, list.count(), chunkSize)
    .map(chunkStart => list.slice(chunkStart, chunkStart + chunkSize))
    .toList();

export function* fetchFormSaga({ payload }) {
  const { error, form } = yield call(fetchForm, {
    kappSlug: payload.kappSlug,
    formSlug: payload.formSlug,
    include: FORM_INCLUDES,
  });

  if (error) {
    yield put(actions.fetchFormFailure(error));
  } else {
    yield put(actions.fetchFormSuccess(form));
  }
}

export function* cloneFormSaga({ payload }) {
  const { error: cloneError, form: cloneForm } = yield call(fetchForm, {
    kappSlug: payload.kappSlug,
    formSlug: payload.cloneFormSlug,
    include: FORM_FULL_INCLUDES,
  });

  if (cloneError) {
    addToastAlert({
      title: 'Error Cloning Form',
      message: 'Could not find form to clone.',
    });
    yield put(actions.cloneFormComplete(payload));
  } else {
    const { error, form } = yield call(updateForm, {
      kappSlug: payload.kappSlug,
      formSlug: payload.formSlug,
      form: {
        bridgedResources: cloneForm.bridgedResources,
        customHeadContent: cloneForm.customHeadContent,
        pages: cloneForm.pages,
        securityPolicies: cloneForm.securityPolicies,
        attributesMap: cloneForm.attributesMap,
        categorizations: cloneForm.categorizations,
      },
    });

    if (error) {
      addToastAlert({
        title: 'Error Cloning Form',
        message: error.message,
      });
      yield put(actions.cloneFormComplete(payload));
    }

    addToast(`${form.name} cloned successfully from ${cloneForm.name}`);
    if (typeof payload.callback === 'function') {
      payload.callback(form);
    }
    yield put(actions.cloneFormComplete(payload));
  }
}

export function* fetchSubmissionSaga({ payload }) {
  const { submission, error } = yield call(fetchSubmission, {
    id: payload.id,
    include: SUBMISSION_INCLUDES,
  });
  if (error) {
    yield put(actions.fetchSubmissionFailure(error));
  } else {
    yield put(actions.fetchSubmissionSuccess(submission));
  }
}

export function* fetchCloneSubmissionSaga({ payload }) {
  const { submission, error } = yield call(fetchSubmission, {
    id: payload.id,
    include: SUBMISSION_INCLUDES,
  });
  if (error) {
    yield put(actions.fetchCloneSubmissionFailure(error));
  } else {
    yield put(actions.fetchCloneSubmissionSuccess(submission));
  }
}

export function* fetchSubmissionsSaga(action) {
  const { formSlug } = action.payload;

  let createdAt = undefined;
  let data = [];

  do {
    const { submissions, error } = yield call(searchSubmissions, {
      search: {
        q: createdAt
          ? defineKqlQuery()
              .lessThanOrEquals('createdAt', 'createdAt')
              .end()({ createdAt: createdAt })
          : '',
        include: ['details', 'values'],
        limit: 1000,
        orderBy: 'createdAt',
        direction: 'DESC',
      },
      form: formSlug,
      kapp: 'admin',
    });

    if (error) {
      yield put(actions.fetchSubmissionsFailure(error));
      break;
    } else {
      createdAt =
        submissions && submissions.length === 1000
          ? submissions[999].createdAt
          : undefined;
      data = data.concat(
        createdAt
          ? submissions.filter(s => s.createdAt !== createdAt)
          : submissions,
      );
    }
  } while (createdAt);
  yield put(actions.fetchSubmissionsSuccess(data));
}

export function* deleteFormSaga({ payload }) {
  const { form, error } = yield call(deleteForm, {
    kappSlug: payload.kappSlug,
    formSlug: payload.formSlug,
  });
  if (form) {
    if (typeof payload.onSuccess === 'function') {
      yield call(payload.onSuccess, form);
    }
    yield put(actions.deleteFormComplete(payload));
    addToast('Form deleted successfully.');
  } else {
    addToastAlert({ title: 'Error Deleting Form', message: error.message });
  }
}

export function* deleteSubmissionSage({ payload }) {
  const { error } = yield call(deleteSubmission, {
    id: payload.id,
  });
  if (!error) {
    if (typeof payload.onSuccess === 'function') {
      yield call(payload.onSuccess);
    }
    addToast('Submission deleted successfully.');
  } else {
    if (typeof payload.onFailure === 'function') {
      yield call(payload.onFailure);
    }
    addToastAlert({
      title: 'Error Deleting Submission',
      message: error.message,
    });
  }
}

export function* executeImportSaga(action) {
  const { form, records, recordsLength } = action.payload;
  let recordsChunk = null;
  // This variable keeps the current row number for use with error display.
  let currentRecordCount = action.currentRecordCount
    ? action.currentRecordCount
    : 0;
  const CHUNK_SIZE = 10;

  // abstract chunking requirement from function call.
  if (!action.beenChunked) {
    recordsChunk = chunkList(records, CHUNK_SIZE);
  } else {
    recordsChunk = records;
  }

  // head is the first element in the List, tail is the rest fo the List.
  const head = recordsChunk.first();
  const tail = recordsChunk.shift();

  yield put(
    actions.debouncePercentComplete({ tail, CHUNK_SIZE, recordsLength }),
  );

  const responses = yield all(
    head
      .map(
        record =>
          record.id
            ? call(updateSubmission, {
                kappSlug: 'admin',
                formSlug: form.slug,
                values: record.values,
                id: record.id,
              })
            : call(createSubmission, {
                kappSlug: 'admin',
                formSlug: form.slug,
                values: record.values,
                completed: false,
              }),
      )
      .toJS(),
  );

  const errorsArray = responses.reduce((acc, response) => {
    currentRecordCount++;
    const { error } = response;

    // Not handling nonconforming errors from CoreAPI.
    if (error) {
      acc.push({
        rowNumber: currentRecordCount,
        errors: [error.message],
      });
    }
    return acc;
  }, []);
  if (errorsArray.length > 0) {
    yield put(actions.setImportFailedCalls(errorsArray));
  }

  if (tail.size > 0) {
    yield call(executeImportSaga, {
      ...action,
      payload: {
        ...action.payload,
        records: tail,
      },
      beenChunked: true,
      currentRecordCount,
    });
  } else {
    yield put(actions.setImportComplete());
  }
}

export function* debouncePrecentCompleteSaga(action) {
  const { tail, CHUNK_SIZE, recordsLength } = action.payload;

  // TODO: There is a bug in the progress bar.
  // That last chunk in the List may be smaller than the CHUNK_SIZE.
  yield put(
    actions.setImportPercentComplete(
      100 - Math.round(((tail.size * CHUNK_SIZE) / recordsLength) * 100),
    ),
  );
}

export function* watchAdminForms() {
  yield takeEvery(types.FETCH_FORM_REQUEST, fetchFormSaga);
  yield takeEvery(types.DELETE_FORM_REQUEST, deleteFormSaga);
  yield takeEvery(types.CLONE_FORM_REQUEST, cloneFormSaga);
  yield takeEvery(types.FETCH_SUBMISSION_REQUEST, fetchSubmissionSaga);
  yield takeEvery(
    types.FETCH_CLONE_SUBMISSION_REQUEST,
    fetchCloneSubmissionSaga,
  );
  yield takeEvery(types.FETCH_SUBMISSIONS_REQUEST, fetchSubmissionsSaga);
  yield takeEvery(types.DELETE_SUBMISSION_REQUEST, deleteSubmissionSage);

  yield takeEvery(types.EXECUTE_IMPORT, executeImportSaga);
  yield throttle(
    1500,
    types.DEBOUNCE_PERCENT_COMPLETE,
    debouncePrecentCompleteSaga,
  );
}
